import React, {
  ChangeEvent,
  ChangeEventHandler,
  Component,
  Dispatch,
  forwardRef,
  JSXElementConstructor,
  MutableRefObject,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import getIn from '../connectedForm/utils/getIn';
import SelectAll from './formatters/SelectAll';
import AppConstants from './AppConstants';
import { isCtrlKeyHeldDown, isKeyPrintable, isShiftKeyHeldDown } from './utils/keyboardUtils';
import isFunction from './utils/isFunction';

import BaseGrid from './Grid';
import { Direction, Maybe } from '../../graphql/schema/p4types';
import { ICommit, IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import { ColumnMetricsType } from './ColumnMetrics';
import { useRefFunction } from '../../utils/hooks';
import { IRowUpdatedFunction } from '../EditableDataGrid/EditableDataGrid';
import ReactDataGridContext from '../Context/ReactDataGridContext';
import ReactDataGridCheckboxEditor from './editors/ReactDataGridCheckboxEditor';
import { ReorderHeader } from './ReorderHeader';

const RowUtils = require('./RowUtils');
const ColumnUtils = require('./ColumnUtils');
const KeyCodes = require('./KeyCodes');
const ColumnMetrics = require('./ColumnMetrics');
const getScrollbarSize = require('./getScrollbarSize');
require('styles/react-data-grid/react-data-grid-core.css');
require('styles/react-data-grid/react-data-grid-checkbox.css');

type SelectedType = {
  rowIdx: number;
  idx: number;
  active: boolean;
  contextMenuDisplayed?: Maybe<ReactElement>;
  exitedLeft?: boolean;
  event?: MouseEvent;
};

type ColumnEvent = {
  name: string;
  rowIdx: number;
  rowId: number;
  idx: number;
};

type DraggedType = {
  idx: number;
  rowIdx: number;
  value: string;
};

type ICellCommit = ICommit<any> & {
  keyCode: string;
  changed: { expandedHeight: number };
  key?: string;
  shift?: boolean;
  searchTabOffset?: any;
  searchEnterOffset?: any;
  setInactive?: boolean;
};

export type IRowSelection = {
  selectBy:
    | { indexes: Array<string | number> }
    | { isSelectedKey: string }
    | { keys: { values: Array<any>; rowKey: string } }
    | { all: boolean; indexes: Array<string> };
  onRowsSelected?: Function;
  onRowsDeselected?: Function;
  enableShiftSelect?: boolean;
  onAllRowsSelected?: (all: boolean) => void;
  showCheckbox?: boolean;
  checkboxSide?: string;
};

//Separated these out because they're partial on the wrapping component and required on the main component.
interface IReactDataGridDefaultedProps {
  rowKey: string;
  rowHeight: number;
}

export type IOnPressKeyOptions = {
  selected: SelectedType;
  setSelected: (newSelected: SelectedType) => void;
  moveSelectedCell: (e: Event, rowDelta: number, cellDelta: number) => void;
  rowGetter?: (rowIdx: number) => any;
};

type IReactDataGridProps = {
  columns: Array<IEditableDataGridColumnDef>;
  sortColumn?: string;
  sortDirection?: Direction;
  minWidth?: number;
  onColumnResize: Function;
  ignoreKeypresses?: boolean;
  onGridKeyDown?: Function;
  onGridKeyUp?: Function;
  selected: SelectedType;
  setSelected: Dispatch<React.SetStateAction<SelectedType>>;
  onCellSelected: Function;
  onCellDeSelected?: Function;
  onRowClick: Function;
  onRowDoubleClick?: Function;
  onRowRightClick?: Function;
  rowGetter: (rowIdx: number) => any;
  contextMenu: Maybe<ReactElement>;
  rowsCount: number;
  enableCellSelect?: boolean;
  enableCellSelectColumns?: Array<string>;
  onGridRowsUpdated?: (row: any) => void;
  onRowUpdated?: IRowUpdatedFunction<any>;
  getTabOptions?: (commit: any) => { xOffset: number } | null;
  onClearFilters?: Function;
  onDragHandleDoubleClick?: Function;
  onCellExpand?: Function;
  onRowExpandToggle?: Function;
  onCellsDragged?: Function;
  onCellCopyPaste?: Function;
  onGridSort: Function;
  rowSelection: IRowSelection;
  enableRowSelect?: boolean | string;
  enableSelectAll?: boolean;
  onRowSelect?: Function;
  headerRowHeight?: number;
  onAddFilter?: Function;
  headerFiltersHeight?: number;
  onCheckCellIsEditable?: Function;
  onBeforeEdit?: Function;
  selectAllRenderer?: JSXElementConstructor<any>;
  toolbar?: ReactElement;
  getCellActions?: Function;
  onDeleteSubRow?: Function;
  onAddSubRow?: Function;
  enableCellAutoFocus?: boolean;
  gridType?: string;
  testId?: string;
  minHeight?: Maybe<number>;
  rowScrollTimeout?: number;
  scrollToRowIndex?: number;
  contextKey?: string;
  overScan?: any;
  includeHeaderRow?: boolean;
  disableVirtualRows?: boolean;
  onFilter?: Function;
  enableDragAndDrop?: boolean;
  draggableHeaderCell?: Function;
  getValidFilterValues?: Function;
  moveColumn?: Function;
  findColumn?: Function;
  emptyRowsView?: Maybe<ReactNode>;
  dataid?: string;
  columnMetrics: ColumnMetricsType;
  getGridWidth: () => number;
  gridRef: React.MutableRefObject<any>;
  handleSort: Function;
  onPressKey?: {
    numpadAdd: (e: KeyboardEvent, options: IOnPressKeyOptions) => void;
  };
  keysDownRef: MutableRefObject<any>;
  selectedRows: Array<any>;
  ignoreContainerWidth?: boolean;
};

type IReactDataGridState = {
  copied: any;
  expandedRows: Array<any>;
  canFilter: boolean;
  dragged: any;
  scrollOffset: number;
  hoveredRowIdx: number;
  textToCopy: Maybe<string>;
};

class ReactDataGrid extends Component<IReactDataGridProps & IReactDataGridDefaultedProps, IReactDataGridState> {
  private row: any; //ref field.
  private filterRow: any; //ref field.

  static defaultProps = {
    enableCellSelect: false,
    enableCellSelectColumns: [],
    headerFiltersHeight: 45,
    enableRowSelect: false,
    enableSelectAll: true,
    minHeight: 350,
    rowScrollTimeout: 0,
    scrollToRowIndex: 0,
    overScan: {
      colsStart: 5,
      colsEnd: 5,
      rowsStart: 5,
      rowsEnd: 5,
    },
    enableCellAutoFocus: false,
    onBeforeEdit: () => {},
    includeHeaderRow: true,
  };

  constructor(props: IReactDataGridProps & IReactDataGridDefaultedProps, context: unknown) {
    super(props, context);

    this.state = {
      copied: null,
      expandedRows: [],
      canFilter: false,
      dragged: null,
      scrollOffset: 0,
      hoveredRowIdx: -1,
      textToCopy: null,
    };
  }

  getColumn = (idx: number) => {
    const columns = this.props.columnMetrics.columns;
    if (Array.isArray(columns)) {
      return columns[idx];
    }
  };

  onKeyDown = (e: KeyboardEvent) => {
    if (this.props.ignoreKeypresses) return;

    if (isCtrlKeyHeldDown(e)) {
      this.checkAndCall('onPressKeyWithCtrl', e);
    } else if (isShiftKeyHeldDown(e)) {
      switch (e.code) {
        case 'NumpadAdd':
          //Needs to be handled on a per-grid basis - Expected use is row duplication.
          this.props.onPressKey?.numpadAdd?.(e, {
            selected: this.props.selected,
            setSelected: this.props.setSelected,
            moveSelectedCell: this.moveSelectedCell,
            rowGetter: this.props.rowGetter,
          });
          break;
      }
      //Slightly different to the ctrl one - This seems more natural.
      if (e.key === 'Enter' || e.key === 'Tab') {
        this.checkAndCall(`onPressShift${e.key}`, e);
      } else if (isKeyPrintable(e.keyCode)) {
        this.checkAndCall('onPressChar', e);
      }
    } else if (this.isKeyExplicitlyHandled(e.key)) {
      // break up individual keyPress events to have their own specific callbacks
      const callBack = `onPress${e.key}`;
      this.checkAndCall(callBack, e);
    } else if (isKeyPrintable(e.keyCode)) {
      this.checkAndCall('onPressChar', e);
    }

    // Track which keys are currently down for shift clicking etc
    this.props.keysDownRef.current = this.props.keysDownRef.current || {};
    this.props.keysDownRef.current[e.keyCode] = true;
    if (isFunction(this.props.onGridKeyDown)) {
      this.props.onGridKeyDown(e);
    }
  };

  onKeyUp = (e: KeyboardEvent) => {
    // Track which keys are currently down for shift clicking etc
    this.props.keysDownRef.current = this.props.keysDownRef.current || {};
    delete this.props.keysDownRef.current[e.keyCode];

    if (isFunction(this.props.onGridKeyUp)) {
      this.props.onGridKeyUp(e);
    }
  };

  isKeyDown = (keyCode: string) => {
    if (!this.props.keysDownRef.current) return false;
    return keyCode in this.props.keysDownRef.current;
  };

  checkAndCall = (methodName: string, args: any) => {
    if (typeof (this as any)[methodName] === 'function') {
      (this as any)[methodName](args);
    }
  };

  isKeyExplicitlyHandled = (key: string): boolean => {
    return typeof (this as any)[`onPress${key}`] === 'function';
  };

  hasSelectedCellChanged = (newSelected: SelectedType) => {
    return (
      this.props.selected.rowIdx !== newSelected.rowIdx ||
      this.props.selected.idx !== newSelected.idx ||
      !this.props.selected.active
    );
  };

  onContextMenuHide = () => {
    document.removeEventListener('click', this.onContextMenuHide);
    this.props.setSelected((currentSelected) => ({ ...currentSelected, contextMenuDisplayed: undefined }));
  };

  onColumnEvent = (ev: Event, columnEvent: ColumnEvent) => {
    const { idx, name } = columnEvent;

    if (name && typeof idx !== 'undefined') {
      const column = this.getColumn(idx);

      if (column && column.events && column.events[name] && typeof column.events[name] === 'function') {
        const eventArgs = {
          idx,
          rowIdx: columnEvent.rowIdx,
          rowId: columnEvent.rowId,
          column,
        };

        column.events[name](ev, eventArgs);
      }
    }
  };

  onSelect = (newSelected: Omit<SelectedType, 'active'>, setActive = false, ignoreRowBounds = false) => {
    if (
      this.props.selected.rowIdx !== newSelected.rowIdx ||
      this.props.selected.idx !== newSelected.idx ||
      !this.props.selected.active
    ) {
      const idx = newSelected.idx;
      const rowIdx = newSelected.rowIdx;
      if (this.isCellWithinBounds(newSelected, ignoreRowBounds)) {
        const oldSelection = this.props.selected;
        this.props.setSelected({ ...newSelected, active: false });
        if (typeof this.props.onCellDeSelected === 'function') {
          this.props.onCellDeSelected(oldSelection);
        }
        if (typeof this.props.onCellSelected === 'function') {
          this.props.onCellSelected(newSelected);
        }
        if (setActive) {
          this.setActive(undefined, newSelected);
        }
      } else if (
        (rowIdx === -1 && idx === -1) ||
        (rowIdx >= 0 && idx === ColumnUtils.getSize(this.props.columnMetrics.columns))
      ) {
        // When it's outside the grid, set rowIdx anyway (Or when it's the comment field, which matches rowIdx & is +1 on the number of columns).
        this.props.setSelected({ idx, rowIdx, active: false });
      }
    } else if (setActive) this.setActive(undefined, newSelected);
  };

  onCellClick = (cell: SelectedType, e: Event) => {
    if (!this.isFocusedOnTable(e.target)) {
      return;
    }

    this.onSelect({ rowIdx: cell.rowIdx, idx: cell.idx }, true);

    if (this.props.onRowClick && typeof this.props.onRowClick === 'function') {
      this.props.onRowClick(cell.rowIdx, this.props.rowGetter(cell.rowIdx), this.getColumn(cell.idx));
    }
    if (e) {
      e.stopPropagation();
    }
  };

  onCellFocus = (cell: SelectedType, e) => {
    if (!this.isFocusedOnTable(e.target)) {
      return;
    }

    this.onSelect(cell);
  };

  onCellContextMenu = (cell: SelectedType) => {
    const rowData = this.props.rowGetter(cell.rowIdx);
    this.props.onRowRightClick?.(cell.event, rowData);
    this.onSelect({
      rowIdx: cell.rowIdx,
      idx: cell.idx,
      contextMenuDisplayed: this.props.contextMenu,
    });
    if (this.props.contextMenu) {
      document.addEventListener('click', this.onContextMenuHide);
    }
  };

  onCellDoubleClick = (cell: SelectedType, e: Event) => {
    this.onSelect({ rowIdx: cell.rowIdx, idx: cell.idx });
    if (this.props.onRowDoubleClick && typeof this.props.onRowDoubleClick === 'function') {
      this.props.onRowDoubleClick(cell.rowIdx, this.props.rowGetter(cell.rowIdx), this.getColumn(cell.idx));
    }
    this.setActive(undefined, cell);
    if (e) {
      e.stopPropagation();
    }
  };

  onPressArrowUp = (e: Event) => {
    this.moveSelectedCell(e, -1, 0);
  };

  onPressArrowDown = (e: Event) => {
    this.moveSelectedCell(e, 1, 0);
  };

  onPressArrowLeft = (e: Event) => {
    this.moveSelectedCell(e, 0, -1);
  };

  onPressArrowRight = (e: Event) => {
    this.moveSelectedCell(e, 0, 1);
  };

  isFocusedOnCell = () => {
    return (
      //This stops it from tabbing to row 0 when you tab from a comment.
      document.activeElement?.classList &&
      (document.activeElement.classList.contains('react-grid-Cell') ||
        document.activeElement.classList.contains('react-grid-Comment'))
    );
  };

  /**
   * Check if the given element is within the DOM for the grid.
   * (If no element is provided, checks the current active element - Legacy support).
   *
   * This is to help us ignore clicks/key-presses on Modals/etc that should not be triggering background changes to the grid cells.
   */
  isFocusedOnTable = (element = document.activeElement): boolean => {
    if (this.props.ignoreKeypresses) {
      return false;
    }
    const domNode = this.props.gridRef.current;
    return domNode && domNode.contains(element);
    return domNode && domNode.contains(element);
  };

  hasClickedOnDatePicker = (ev: MouseEvent) => {
    return (
      // This is used to prevent the data grid dropping focus when you click into a date picker.
      ev.target?.classList && ev.target.classList.value.includes('react-datepicker')
    );
  };

  hasClickedOnDatePicker = (ev: MouseEvent) => {
    return (
      // This is used to prevent the data grid dropping focus when you click into a date picker.
      ev.target?.classList && ev.target.classList.value.includes('react-datepicker')
    );
  };

  exitGrid = (oldSelectedCell: SelectedType, newSelectedValue: SelectedType) => {
    this.props.setSelected(newSelectedValue);

    if (typeof this.props.onCellDeSelected === 'function') {
      this.props.onCellDeSelected(oldSelectedCell);
    }
  };

  enterGrid = (newSelectedValue: any) => {
    this.props.setSelected(newSelectedValue);

    if (typeof this.props.onCellSelected === 'function') {
      this.props.onCellSelected(newSelectedValue);
    }
  };

  onPressShiftTab = (e: KeyboardEvent) => {
    //Main 'onPressTab' function handles the shift key.
    this.onPressTab(e);
  };

  onPressTab = (e: KeyboardEvent) => {
    // Scenario 0a: When there are no rows in the grid, pressing tab needs to allow the browser to handle it
    if (this.props.rowsCount === 0) {
      return;
    }
    // Scenario 0b: When we're editing a cell
    const idx = this.props.selected.idx;
    const rowIdx = this.props.selected.rowIdx;

    if (this.props.selected.active) {
      // if we are in a position to leave the grid, stop editing but stay in that cell
      if (this.canExitGrid(e)) {
        this.moveSelectedCell(e, 0, 0);
        return;
      }
      // otherwise move left or right as appropriate
      if (e.shiftKey) {
        this.moveSelectedCell(e, 0, -1);
      } else {
        const tabOptions = getIn(this.getColumn(this.props.selected.idx), 'options.searchTabOffset');
        this.moveSelectedCell(e, tabOptions ? tabOptions.yOffset : 0, tabOptions ? tabOptions.xOffset : 1);
      }
      return;
    }

    const shift = e.shiftKey;
    // Scenario 1: we're at a cell where we can exit the grid
    if (this.canExitGrid(e) && this.isFocusedOnCell()) {
      if (shift && idx >= 0) {
        this.exitGrid({ idx, rowIdx, active: false }, { idx: -1, rowIdx, exitedLeft: true, active: false });
        return;
      } else if (!shift && idx >= 0) {
        this.exitGrid({ idx, rowIdx, active: false }, { idx: -1, rowIdx, active: false });
        return;
      }
    }
    // Scenario 2: we're on the div surrounding the grid and press shift+Tab
    // and we just exited left, so we want to let the browser handle it
    // KNOWN ISSUE: Focus on the table can come from either side and at this point we can't know how
    // they user arrived, so it is possible that exitLeft gets set and then the user clicks out of the table
    // and they won't be able to Shift+Tab around the site to re-enter the table from the right.
    if (this.isFocusedOnTable() && !this.isFocusedOnCell() && shift && this.props.selected.exitedLeft) {
      this.enterGrid({ idx, rowIdx });
      return;
    }
    // Scenario 3: we're on the div surrounding the grid and we want to enter the grid
    if (!this.isFocusedOnCell()) {
      // Scenario 3A: idx has been set to -1 (eg can happen when clicking into the filter box)
      // we want to go to the first cell in the row if we press Tab
      // we want to go to the last cell in the row if we press Shift+Tab
      if (idx === -1) {
        this.moveSelectedCell(e, rowIdx === -1 ? 1 : 0, shift ? this.getNbrColumns() : 1);
        return;
      }
      // otherwise, there is a selected cell in the table already, and
      // we want to trigger it to focus - setting selected in state will update
      // the cell props, and checkFocus will be called
      this.enterGrid({ idx, rowIdx, changeSomething: true });
      // make sure the browser doesn't handle it
      e.preventDefault();
      return;
    }

    if (e.shiftKey) {
      this.moveSelectedCell(e, 0, -1);
    } else {
      const tabOptions = getIn(this.getColumn(this.props.selected.idx), 'options.searchTabOffset');
      this.moveSelectedCell(e, tabOptions ? tabOptions.yOffset : 0, tabOptions ? tabOptions.xOffset : 1);
    }
  };

  /**
   * This function triggers *any time* the user clicks outside the grid - Need to take care that the logic
   * within only triggers if we're actually 'leaving' the grid with this click.
   */
  handleClickOutside(ev: MouseEvent) {
    const idx = this.props.selected.idx;
    const rowIdx = this.props.selected.rowIdx;

    if ((idx > 0 || this.isFocusedOnCell()) && !this.isFocusedOnTable() && !this.hasClickedOnDatePicker(ev)) {
      //Debugging note - Be aware that if there's multiple grids on screen this will run when clicking between the 2 grids! (Which is intended/expected).
      this.exitGrid({ idx, rowIdx, active: false }, { idx: -1, rowIdx: rowIdx, active: false });
    }
  }

  onPressEnter = (e: KeyboardEvent) => {
    if (this.props.selected?.active) {
      this.props.setSelected((selected) => ({ ...selected, active: false }));
      return;
    }

    this.onCellClick(this.props.selected, e);
  };

  onPressShiftEnter = (e: KeyboardEvent) => {
    if (this.props.selected?.active) {
      this.props.setSelected((selected) => ({ ...selected, active: false }));
    }
    this.moveSelectedCell(e, 1, -this.props.selected.idx);
  };

  onPressDelete = (e: KeyboardEvent) => {
    this.setActive(e.key);
  };

  onPressEscape = (_e: KeyboardEvent) => {
    this.setInactive();
    this.handleCancelCopy();
  };

  onPressBackspace = (e: KeyboardEvent) => {
    this.setActive(e.key);
  };

  onPressChar = (e: KeyboardEvent) => {
    if (isKeyPrintable(e.keyCode)) {
      this.setActive(e.keyCode);
    }
  };

  onPressKeyWithCtrl = (e: KeyboardEvent) => {
    const cKeys = [99, 67];
    const vKeys = [86, 118];

    const rowIdx = this.props.selected.rowIdx;
    const row = this.props.rowGetter(rowIdx);

    const idx = this.props.selected.idx;
    const col = this.getColumn(idx);

    const cellEditable = this.isCellEditable(col as IEditableDataGridColumnDef);

    if (ColumnUtils.canEdit(col, row, cellEditable)) {
      if (e.keyCode === cKeys[0] || e.keyCode === cKeys[1]) {
        const value = this.getSelectedValue();
        this.handleCopy({ value: value });
      } else if (e.keyCode === vKeys[0] || e.keyCode === vKeys[1]) {
        this.handlePaste();
      }
    }
  };

  onGridRowsUpdated = (cellKey: string, fromRow: number, toRow: number, updated: any, action: any, originRow?: any) => {
    const rowIds: Array<any> = [];

    for (let i = fromRow; i <= toRow; i++) {
      rowIds.push(this.props.rowGetter(i)[this.props.rowKey]);
    }

    const fromRowData = this.props.rowGetter(action === 'COPY_PASTE' ? originRow : fromRow);
    const fromRowId = fromRowData[this.props.rowKey];
    const toRowId = this.props.rowGetter(toRow)[this.props.rowKey];

    this.props.onGridRowsUpdated?.({
      cellKey,
      fromRow,
      toRow,
      fromRowId,
      toRowId,
      rowIds,
      updated,
      action,
      fromRowData,
    });
  };

  onCellCommit = (commit: ICellCommit) => {
    //Ignore commit if 'changed' field is undefined - Should be either a proper value or 'null' if clearing a value.
    if (this.props.onRowUpdated && commit.updated?.[commit.cellKey as string] !== undefined) {
      if (!commit.cellKeys) {
        commit.cellKeys = [];
      }
      if (!commit.cellKeys.includes(commit.cellKey)) {
        commit.cellKeys.push(commit.cellKey);
      }
      this.props.onRowUpdated(commit as ICommit<any>);
    }

    const previousRowIdx = this.props.selected.rowIdx;
    const previousColIdx = this.props.selected.idx;
    const tabOptions = this.props.getTabOptions?.(commit);

    if ((commit.key === 'Enter' || commit.updated?.lastKeyCode === 13) && commit.shift) {
      this.scrollAndSelect(0, previousRowIdx + 1);
    } else if (tabOptions) {
      this.scrollAndSelect(previousColIdx + tabOptions.xOffset, previousRowIdx);
    } else if (
      commit.updated?.lastKeyCode < 14 &&
      commit.searchTabOffset !== undefined &&
      commit.searchEnterOffset !== undefined
    ) {
      switch (commit.updated?.lastKeyCode) {
        case 9: //tab
          this.scrollAndSelect(
            previousColIdx + commit.searchTabOffset.xOffset,
            previousRowIdx + commit.searchTabOffset.yOffset
          );
          break;
        case 'click':
        case 13: //enter
          this.scrollAndSelect(
            previousColIdx + commit.searchEnterOffset.xOffset,
            previousRowIdx + commit.searchEnterOffset.yOffset
          );
          break;
      }
    } else if (commit.updated?.lastKeyCode && commit.updated?.lastKeyCode < 14) {
      switch (commit.updated?.lastKeyCode) {
        case 9: //tab
          this.scrollAndSelect(previousColIdx + 1, previousRowIdx);
          break;
        case 'click':
        case 13: //enter
          this.scrollAndSelect(previousColIdx, previousRowIdx + 1);
          break;
      }
    } else if (commit.updated?.lastKeyCode?.type === 'mousedown') {
      this.props.setSelected({ idx: previousColIdx, rowIdx: previousRowIdx, active: true });
    } else if (commit.setInactive) {
      this.props.setSelected((selected) => ({ ...selected, active: false }));
    }
  };

  scrollAndSelect = (newColIdx: number, newRowIdx: number, { setActive = undefined, ignoreRowBounds = true } = {}) => {
    this.scrollToColumn(newColIdx);
    this.onSelect({ idx: newColIdx, rowIdx: newRowIdx }, setActive, ignoreRowBounds);
  };

  onDragStart = (e: DragEvent) => {
    const idx = this.props.selected.idx;
    // To prevent dragging down/up when reordering rows.
    const isViewportDragging = e && e.target && (e.target as any).className;
    if (idx > -1 && isViewportDragging) {
      const value = this.getSelectedValue();
      this.handleDragStart({
        idx: this.props.selected.idx,
        rowIdx: this.props.selected.rowIdx,
        value: value,
      });
      // need to set dummy data for FF
      if (e && e.dataTransfer) {
        if (e.dataTransfer.setData) {
          e.dataTransfer.dropEffect = 'move';
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', '');
        }
      }
    }
  };

  onToggleFilter = () => {
    // setState() does not immediately mutate this.state but creates a pending state transition.
    // Therefore if you want to do something after the state change occurs, pass it in as a callback function.
    this.setState({ canFilter: !this.state.canFilter }, () => {
      if (!this.state.canFilter && this.props.onClearFilters) {
        this.props.onClearFilters();
      }
    });
  };

  onDragHandleDoubleClick = (e: { idx: number; rowIdx: number; rowData: any }) => {
    if (this.props.onDragHandleDoubleClick) {
      this.props.onDragHandleDoubleClick(e);
    }

    if (this.props.onGridRowsUpdated) {
      const cellKey = this.getColumn(e.idx)?.key as string;
      if (cellKey) {
        this.onGridRowsUpdated(
          cellKey,
          e.rowIdx,
          this.props.rowsCount - 1,
          { [cellKey]: e.rowData[cellKey] },
          AppConstants.UpdateActions.COLUMN_FILL
        );
      }
    }
  };

  onCellExpand = (args: any) => {
    if (this.props.onCellExpand) {
      this.props.onCellExpand(args);
    }
  };

  onRowExpandToggle = (args: any) => {
    if (typeof this.props.onRowExpandToggle === 'function') {
      this.props.onRowExpandToggle(args);
    }
  };

  isCellWithinBounds = ({ idx, rowIdx }: { idx: number; rowIdx: number }, ignoreRowBounds?: boolean) => {
    return (
      idx >= 0 &&
      rowIdx >= 0 &&
      idx < ColumnUtils.getSize(this.props.columnMetrics.columns) &&
      (ignoreRowBounds || rowIdx < this.props.rowsCount)
    );
  };

  handleDragStart = (dragged: DraggedType) => {
    if (!this.dragEnabled()) {
      return;
    }
    if (this.isCellWithinBounds(dragged)) {
      this.setState({ dragged: dragged });
    }
  };

  handleDragEnd = () => {
    if (!this.dragEnabled()) {
      return;
    }
    const { selected } = this.props;
    const { dragged } = this.state;
    const column = this.getColumn(selected.idx);
    if (selected && dragged && column) {
      const cellKey = column.key;
      const fromRow = selected.rowIdx < dragged.overRowIdx ? selected.rowIdx : dragged.overRowIdx;
      const toRow = selected.rowIdx > dragged.overRowIdx ? selected.rowIdx : dragged.overRowIdx;
      if (this.props.onCellsDragged) {
        this.props.onCellsDragged({
          cellKey: cellKey,
          fromRow: fromRow,
          toRow: toRow,
          value: dragged.value,
        });
      }
      if (this.props.onGridRowsUpdated && cellKey) {
        this.onGridRowsUpdated(
          cellKey as string,
          fromRow,
          toRow,
          { [cellKey]: dragged.value },
          AppConstants.UpdateActions.CELL_DRAG
        );
      }
    }
    this.setState({ dragged: { complete: true } });
  };

  handleDragEnter = (row: any) => {
    if (!this.dragEnabled() || !this.state.dragged) {
      return;
    }
    const dragged = this.state.dragged;
    dragged.overRowIdx = row;
    this.setState({ dragged: dragged });
  };

  handleTerminateDrag = () => {
    if (!this.dragEnabled()) {
      return;
    }
    this.setState({ dragged: null });
  };

  handlePaste = () => {
    if (!this.copyPasteEnabled() || !this.state.copied) {
      return;
    }
    const { selected } = this.props;
    const cellKey = this.getColumn(selected.idx)?.key;
    const textToCopy = this.state.textToCopy;
    const fromRow = this.state.copied.rowIdx;
    const toRow = selected.rowIdx;

    if (cellKey) {
      if (this.props.onCellCopyPaste) {
        this.props.onCellCopyPaste({
          cellKey: cellKey,
          rowIdx: toRow,
          value: textToCopy,
          fromRow: fromRow,
          toRow: toRow,
        });
      }

      if (this.props.onGridRowsUpdated) {
        this.onGridRowsUpdated(
          cellKey as string,
          toRow,
          toRow,
          { [cellKey]: textToCopy },
          AppConstants.UpdateActions.COPY_PASTE,
          fromRow
        );
      }
    }
  };

  handleCancelCopy = () => {
    this.setState({ copied: null });
  };

  handleCopy = (args: { value: string }) => {
    if (!this.copyPasteEnabled()) {
      return;
    }
    const textToCopy = args.value;
    const { selected } = this.props;
    const copied = { idx: selected.idx, rowIdx: selected.rowIdx };
    this.setState({ textToCopy: textToCopy, copied: copied });
  };

  getRowOffsetHeight = (): number => {
    let offsetHeight = 0;
    this.getHeaderRows().forEach((row) => (offsetHeight += parseFloat(row.height as any)));
    return offsetHeight;
  };

  getHeaderRows = (): Array<{
    ref: any;
    height?: number;
    rowType: 'header' | 'filter';
    filterable?: boolean;
    onFilterChange?: Function;
  }> => {
    const rows: Array<{
      ref: any;
      height?: number;
      rowType: 'header' | 'filter';
      filterable?: boolean;
      onFilterChange?: Function;
    }> = [
      {
        ref: (node: any) => (this.row = node),
        height: this.props.headerRowHeight || this.props.rowHeight,
        rowType: 'header',
      },
    ];
    if (this.state.canFilter) {
      rows.push({
        ref: (node: any) => (this.filterRow = node),
        filterable: true,
        onFilterChange: this.props.onAddFilter,
        height: this.props.headerFiltersHeight,
        rowType: 'filter',
      });
    }
    return rows;
  };

  getRowSelectionProps = () => {
    if (this.props.rowSelection) {
      return this.props.rowSelection.selectBy;
    }

    return null;
  };

  getSelectedRows = () => {
    if (this.props.rowSelection) {
      return null;
    }

    return this.props.selectedRows.filter((r) => r.isSelected === true);
  };

  getSelectedValue = (): string => {
    const rowIdx = this.props.selected.rowIdx;
    const idx = this.props.selected.idx;
    const cellKey = this.getColumn(idx)?.key;
    const row = this.props.rowGetter(rowIdx);
    return RowUtils.get(row, cellKey);
  };

  canExitGrid = (e: KeyboardEvent): boolean => {
    const atLastCellInRow = this.isAtOrBeyondLastCellInRow();
    const atFirstCellInRow = this.isAtFirstCellInRow();
    const atLastRow = this.isAtLastRow();
    const atFirstRow = this.isAtFirstRow();
    const shift = e.shiftKey;

    if (shift) {
      if (atFirstCellInRow && atFirstRow) {
        return true;
      }
    } else if (atLastCellInRow && atLastRow) {
      return true;
    }
    return false;
  };

  /**
   * Shift the currently focused cell (Not necessarily active) by the give row/cell delta's.
   *  (Typically this is a tab (0, 1) or shift-tab (0, -1) but there are exceptions like shift+enter.
   * @param e - Event that triggered the navigation, usually a keyboard event (tab/enter/etc).
   * @param rowDelta - How much to shift the row index by (+ve moves the focus down).
   * @param cellDelta - How much to shift the cell index by (+ve moves the focus right).
   */
  moveSelectedCell = (e: Event, rowDelta: number, cellDelta: number) => {
    // we need to prevent default as we control grid scroll
    // otherwise it moves every time you left/right which is janky
    e.preventDefault(); //CJS - Want to disable for description fields :/

    const targetCellIndex = this.props.selected.idx + cellDelta;
    const targetRowIndex = this.props.selected.rowIdx + rowDelta;

    //If rowDelta is positive (Shift+Enter) it's 'shift' should be considered false despite the negative cell delta.
    const nextPosition = this.getNextValidCellPosition(
      targetCellIndex,
      targetRowIndex,
      !(rowDelta > 0) && cellDelta < 0
    );

    if (!nextPosition) {
      //Do nothing - Let the browser handle the tab because we're outside the valid bounds of the grid cells.
      return;
    }

    const { idx, rowIdx } = nextPosition;

    this.scrollToColumn(idx);
    this.onSelect({ idx: idx, rowIdx: rowIdx });
  };

  getNbrColumns = () => {
    return this.props.columnMetrics.columns.length;
  };

  getDataGridDOMNode = () => {
    return this.props.gridRef.current;
  };

  /**
   * If the provided idx/rowIdx are valid, return them.
   * Otherwise, determine the next valid cell position (Typically the next column without 'tabSkip: true') to return.
   * Return undefined if there are no valid columns.
   * @param idx - Cell index to check.
   * @param rowIdx - Row index to check.
   * @param shift - Indicates if the 'shift' key is being held, which means we're reverse-tabbing.
   */
  getNextValidCellPosition = (
    idx: number,
    rowIdx: number,
    shift: boolean
  ): { idx: number; rowIdx: number } | undefined => {
    const cellDelta = shift ? -1 : 1;

    if (rowIdx < 0 || rowIdx > this.props.rowsCount - 1) {
      return undefined;
    }

    //Use columnMetrics.columns - Include checkbox/actions cells for index counting.
    const columns = this.props.columnMetrics.columns;

    const outOfBounds = (!shift && idx > columns.length - 1) || (shift && idx < 0);

    if (columns[idx]?.tabSkip || outOfBounds) {
      if (!shift && idx >= columns.length) {
        return this.getNextValidCellPosition(0, rowIdx + 1, false);
      } else if (shift && idx <= 0) {
        return this.getNextValidCellPosition(columns.length - 1, rowIdx - 1, true);
      }
      return this.getNextValidCellPosition(idx + cellDelta, rowIdx, shift);
    }

    if (this.props.rowGetter(rowIdx)?.archived) {
      const nextRowIdx = rowIdx > this.props.selected.rowIdx ? rowIdx + 1 : rowIdx - 1;
      return this.getNextValidCellPosition(idx, nextRowIdx, shift);
    }

    return { idx, rowIdx };
  };

  isAtOrBeyondLastCellInRow = () => {
    const numberOfColumns = this.getNbrColumns();
    return this.props.selected.idx >= numberOfColumns;
  };

  isAtLastRow = () => {
    return this.props.selected.rowIdx === this.props.rowsCount - 1;
  };

  isAtFirstCellInRow = () => {
    return this.props.selected.idx === 0;
  };

  isAtFirstRow = () => {
    return this.props.selected.rowIdx === 0;
  };

  openCellEditor = (rowIdx: number, idx: number) => {
    const row = this.props.rowGetter(rowIdx);
    const col = this.getColumn(idx);

    const cellEditable = this.isCellEditable(col as IEditableDataGridColumnDef);

    if (!ColumnUtils.canEdit(col, row, cellEditable)) {
      return;
    }

    const newSelected = { rowIdx, idx, active: false };
    if (this.hasSelectedCellChanged(newSelected)) {
      this.props.setSelected(newSelected);
      this.setActive('Enter', newSelected);
    } else {
      this.setActive('Enter');
    }
  };

  isCellEditable(col: IEditableDataGridColumnDef<any> & { left?: number; locked?: boolean; events?: any }) {
    let cellEditable = false;
    if (
      (this.props.enableCellSelect && this.props.enableCellSelectColumns?.length === 0) ||
      (this.props.enableCellSelectColumns &&
        this.props.enableCellSelectColumns.length > 0 &&
        this.props.enableCellSelectColumns?.includes(col.key as string))
    ) {
      cellEditable = true;
    }
    return cellEditable;
  }

  scrollToColumn = (colIdx: number) => {
    if (this.props.gridRef.current) {
      const canvas = this.props.gridRef.current.querySelector('.react-grid-Canvas');
      if (canvas) {
        let left = 0;
        let locked = 0;

        for (let i = 0; i < colIdx; i++) {
          const column = this.getColumn(i);
          if (column?.width) {
            left += column.width;
          }
          if (column?.locked) {
            locked += column.width ? column.width : 0;
          }
        }

        const selectedColumn = this.getColumn(colIdx);
        if (selectedColumn) {
          const scrollLeft = left - locked - canvas.scrollLeft;
          const scrollRight = left + (selectedColumn.width ? selectedColumn.width : 0) - canvas.scrollLeft;

          if (scrollLeft < 0) {
            canvas.scrollLeft += scrollLeft;
          } else if (scrollRight > canvas.clientWidth) {
            const scrollAmount = scrollRight - canvas.clientWidth;
            canvas.scrollLeft += scrollAmount;
          }
        }
      }
    }
  };

  deselect = () => {
    this.props.setSelected({ rowIdx: -1, idx: -1, active: false });
  };

  /**
   * Set a given cell to active, returns true if the cell was set, false otherwise (It may not be editable).
   */
  setActive = (
    keyPressed: number | string | undefined,
    newSelected: Omit<SelectedType, 'active'> = this.props.selected
  ): boolean => {
    const rowIdx = newSelected.rowIdx;
    const row = this.props.rowGetter(rowIdx);

    const idx = newSelected.idx;
    const column = this.getColumn(idx);

    const cellEditable = this.isCellEditable(column as IEditableDataGridColumnDef);

    if (ColumnUtils.canEdit(column, row, cellEditable) && !this.isActive()) {
      const updatedSelected = Object.assign({}, newSelected, {
        idx: idx,
        rowIdx: rowIdx,
        active: true,
        initialKeyCode: keyPressed,
      });
      let showEditor = true;
      if (typeof this.props.onCheckCellIsEditable === 'function') {
        const args = Object.assign({}, { row, column }, updatedSelected);
        showEditor = this.props.onCheckCellIsEditable(args);
      }
      if (showEditor) {
        if (column?.locked) {
          this.props.setSelected(updatedSelected);
        } else {
          this.props.setSelected(updatedSelected);
          this.scrollToColumn(idx);
        }
        this.props.onBeforeEdit?.();
        this.handleCancelCopy();
        return true;
      }
    }
    return false;
  };

  setInactive = () => {
    const rowIdx = this.props.selected.rowIdx;
    const row = this.props.rowGetter(rowIdx);

    const idx = this.props.selected.idx;
    const col = this.getColumn(idx);

    const cellEditable = this.isCellEditable(col as IEditableDataGridColumnDef);

    if (ColumnUtils.canEdit(col, row, cellEditable) && this.isActive()) {
      const updatedSelected = Object.assign({}, this.props.selected, {
        idx: idx,
        rowIdx: rowIdx,
        active: false,
      });
      this.props.setSelected(updatedSelected);
    }
  };

  isActive = (): boolean => {
    return this.props.selected.active;
  };

  copyPasteEnabled = (): boolean => {
    return this.props.onCellCopyPaste !== null;
  };

  dragEnabled = (): boolean => {
    return this.props.onGridRowsUpdated !== undefined || this.props.onCellsDragged !== undefined;
  };

  renderToolbar = (): ReactElement | null => {
    const Toolbar = this.props.toolbar;
    const toolBarProps = {
      columns: this.props.columns,
      onToggleFilter: this.onToggleFilter,
      numberOfRows: this.props.rowsCount,
    };
    if (React.isValidElement(Toolbar)) {
      return React.cloneElement(Toolbar, toolBarProps);
    } else if (isFunction(Toolbar)) {
      const AnyToolbar = Toolbar as any;
      return <AnyToolbar {...toolBarProps} />;
    }
    return null;
  };

  render() {
    const cellMetaData = {
      rowKey: this.props.rowKey,
      selected: this.props.selected,
      dragged: this.state.dragged,
      hoveredRowIdx: this.state.hoveredRowIdx,
      onCellClick: this.onCellClick,
      onCellFocus: this.onCellFocus,
      onCellContextMenu: this.onCellContextMenu,
      onCellDoubleClick: this.onCellDoubleClick,
      onCommit: this.onCellCommit,
      onCommitCancel: this.setInactive,
      copied: this.state.copied,
      handleDragEnterRow: this.handleDragEnter,
      handleTerminateDrag: this.handleTerminateDrag,
      enableCellSelect: this.props.enableCellSelect,
      enableCellSelectColumns: this.props.enableCellSelectColumns,
      onColumnEvent: this.onColumnEvent,
      openCellEditor: this.openCellEditor,
      onDragHandleDoubleClick: this.onDragHandleDoubleClick,
      onCellExpand: this.onCellExpand,
      onRowExpandToggle: this.onRowExpandToggle,
      getDataGridDOMNode: this.getDataGridDOMNode,
      getCellActions: this.props.getCellActions,
      onDeleteSubRow: this.props.onDeleteSubRow,
      onAddSubRow: this.props.onAddSubRow,
      isScrollingVerticallyWithKeyboard: this.isKeyDown(KeyCodes.DownArrow) || this.isKeyDown(KeyCodes.UpArrow),
      isScrollingHorizontallyWithKeyboard:
        this.isKeyDown(KeyCodes.LeftArrow) || this.isKeyDown(KeyCodes.RightArrow) || this.isKeyDown(KeyCodes.Tab),
      enableCellAutoFocus: this.props.enableCellAutoFocus,
      gridType: this.props.gridType,
    };

    const toolbar = this.renderToolbar();
    let containerWidth: number | string = this.props.minWidth || this.props.getGridWidth();
    let gridWidth: number | string = containerWidth - this.state.scrollOffset;

    // depending on the current lifecycle stage, gridWidth() may not initialize correctly
    // this also handles cases where it always returns undefined -- such as when inside a div with display:none
    // eg Bootstrap tab and collapses
    if (typeof containerWidth === 'undefined' || isNaN(containerWidth) || containerWidth === 0) {
      containerWidth = '100%';
    }
    if (typeof gridWidth === 'undefined' || isNaN(gridWidth) || gridWidth === 0) {
      gridWidth = '100%';
    }

    return (
      <div
        data-testid={this.props.testId}
        className="react-grid-Container"
        style={{ width: this.props.ignoreContainerWidth ? undefined : containerWidth }}
        ref={this.props.gridRef}
      >
        {toolbar}
        <div className="react-grid-Main">
          <BaseGrid
            {...this.props}
            rowKey={this.props.rowKey}
            headerRows={this.getHeaderRows()}
            columnMetrics={this.props.columnMetrics}
            rowGetter={this.props.rowGetter}
            rowsCount={this.props.rowsCount}
            rowHeight={this.props.rowHeight}
            cellMetaData={cellMetaData}
            selectedRows={this.getSelectedRows()}
            rowSelection={this.getRowSelectionProps()}
            expandedRows={this.state.expandedRows}
            rowOffsetHeight={this.getRowOffsetHeight()}
            sortColumn={this.props.sortColumn}
            sortDirection={this.props.sortDirection}
            onSort={this.props.handleSort}
            minHeight={this.props.minHeight}
            totalWidth={gridWidth}
            onViewportKeydown={this.onKeyDown}
            onViewportKeyup={this.onKeyUp}
            onViewportDragStart={this.onDragStart}
            onViewportDragEnd={this.handleDragEnd}
            onViewportClick={this.deselect}
            onViewportDoubleClick={this.deselect}
            onColumnResize={this.props.onColumnResize}
            rowScrollTimeout={this.props.rowScrollTimeout}
            scrollToRowIndex={this.props.scrollToRowIndex}
            contextMenu={this.props.contextMenu}
            contextKey={this.props.contextKey}
            overScan={this.props.overScan}
            testId={this.props.testId}
            includeHeaderRow={this.props.includeHeaderRow}
            disableVirtualRows={this.props.disableVirtualRows}
          />
        </div>
      </div>
    );
  }
}

//Hack to make some other code that references '.displayName' work correctly - Eventually should (ideally) refactor that code
(ReactDataGrid as any).displayName = 'ReactDataGrid';

type ICheckboxRowSelectionOptions = {
  rowSelection: IRowSelection;
  enableRowSelect?: boolean | string;
  rowKey: string;
  setSelected: React.Dispatch<React.SetStateAction<SelectedType>>;
  onRowSelect?: Function;
  rowGetter: (rowIdx: number) => any;
};

const useCheckboxRowSelection = ({
  rowSelection,
  enableRowSelect,
  rowKey,
  setSelected,
  onRowSelect,
  rowGetter,
}: ICheckboxRowSelectionOptions) => {
  const [selectedRows, setSelectedRows] = useState<Array<any>>([]);
  const [lastRowIdxUiSelected, setLastRowIdxUiSelected] = useState(-1);

  const keysDownRef = useRef<any>();

  const isSingleKeyDown = (keyCode: string) => {
    if (!keysDownRef.current) return false;
    return keyCode in keysDownRef.current && Object.keys(keysDownRef.current).length === 1;
  };

  // return false if not a shift select so can be handled as normal row selection
  const handleShiftSelect = (rowIdx: number) => {
    if (lastRowIdxUiSelected > -1 && isSingleKeyDown(KeyCodes.Shift)) {
      //We either need a complicated series of type guards to figure out which 'selectBy' is in use or...
      //We can just cast it to any for the moment because it's fine for the destructured properties to be undefined here.
      const { keys, indexes, isSelectedKey } = rowSelection.selectBy as any;
      const isPreviouslySelected = RowUtils.isRowSelected(keys, indexes, isSelectedKey, rowGetter(rowIdx), rowIdx);

      if (isPreviouslySelected) return false;

      let handled = false;

      if (rowIdx > lastRowIdxUiSelected) {
        const rowsSelected: Array<{ rowIdx: number; row: any }> = [];

        for (let i = lastRowIdxUiSelected + 1; i <= rowIdx; i++) {
          if (!RowUtils.isRowSelected(keys, indexes, isSelectedKey, rowGetter(i), i)) {
            rowsSelected.push({ rowIdx: i, row: rowGetter(i) });
          }
        }

        if (typeof rowSelection.onRowsSelected === 'function') {
          rowSelection.onRowsSelected(rowsSelected);
        }

        handled = true;
      } else if (rowIdx < lastRowIdxUiSelected) {
        const rowsSelected: Array<{ rowIdx: number; row: any }> = [];

        for (let i = rowIdx; i <= lastRowIdxUiSelected - 1; i++) {
          if (!RowUtils.isRowSelected(keys, indexes, isSelectedKey, rowGetter(i), i)) {
            rowsSelected.push({ rowIdx: i, row: rowGetter(i) });
          }
        }

        if (typeof rowSelection.onRowsSelected === 'function') {
          rowSelection.onRowsSelected(rowsSelected);
        }

        handled = true;
      }

      if (handled) {
        setLastRowIdxUiSelected(rowIdx);
      }

      return handled;
    }

    return false;
  };

  const getSelectedRow = (rows: Array<any>, key: string) => {
    const selectedRow = rows.filter((r) => {
      return r[rowKey] === key;
    });
    if (selectedRow.length > 0) {
      return selectedRow[0];
    }
  };

  // columnKey not used here as this function will select the whole row,
  // but needed to match the function signature in the ReactDataGridCheckboxEditor
  const handleRowSelect = (rowIdx: number, columnKey: string, rowData: any, e: ChangeEvent) => {
    e.stopPropagation();

    const newRowSelection = rowSelection?.selectBy;

    if (newRowSelection) {
      if (rowSelection.enableShiftSelect === true) {
        if (!handleShiftSelect(rowIdx)) {
          handleNewRowSelect(rowIdx, rowData);
        }
      } else {
        handleNewRowSelect(rowIdx, rowData);
      }
    } else {
      // Fallback to old onRowSelect handler
      const newSelectedRows = enableRowSelect === 'single' ? [] : selectedRows.slice(0);
      const selectedRow = getSelectedRow(newSelectedRows, rowData[rowKey]);
      if (selectedRow) {
        selectedRow.isSelected = !selectedRow.isSelected;
      } else {
        rowData.isSelected = true;
        newSelectedRows.push(rowData);
      }
      setSelected({ rowIdx, idx: 0, active: false });
      setSelectedRows(newSelectedRows);
      if (onRowSelect) {
        onRowSelect(newSelectedRows.filter((r) => r.isSelected === true));
      }
    }
  };

  const handleNewRowSelect = (rowIdx: number, rowData: any) => {
    const { keys, indexes, isSelectedKey } = rowSelection.selectBy as any;
    const isPreviouslySelected = RowUtils.isRowSelected(keys, indexes, isSelectedKey, rowData, rowIdx);

    setSelected({ rowIdx, idx: 0, active: false });

    setLastRowIdxUiSelected(isPreviouslySelected ? -1 : rowIdx);

    if (isPreviouslySelected && typeof rowSelection.onRowsDeselected === 'function') {
      rowSelection.onRowsDeselected([{ rowIdx, row: rowData }]);
    } else if (!isPreviouslySelected && typeof rowSelection.onRowsSelected === 'function') {
      rowSelection.onRowsSelected([{ rowIdx, row: rowData }]);
    }
  };

  return { handleRowSelect, keysDownRef, selectedRows };
};

const useRowCheckboxCell = ({
  rowSelection,
  enableRowSelect,
  enableSelectAll,
}: {
  rowSelection: IRowSelection;
  enableRowSelect?: boolean | string;
  enableSelectAll?: boolean;
}) => {
  //Release/11 (New - Removes extraneous shite
  return useMemo<IEditableDataGridColumnDef | undefined>(() => {
    const handleCheckboxChange: ChangeEventHandler = (e) => {
      //Deselect all rows.
      rowSelection.onAllRowsSelected?.(e.currentTarget instanceof HTMLInputElement && e.currentTarget.checked);
    };

    let headerRenderer: ReactNode;
    if (enableRowSelect === 'single') {
      headerRenderer = <ReorderHeader />;
    } else if (!enableSelectAll) {
      headerRenderer = null;
    } else {
      headerRenderer = <SelectAll onChange={handleCheckboxChange} />;
    }

    return {
      key: 'select-row',
      name: '',
      formatter: ReactDataGridCheckboxEditor,
      filterable: false,
      headerRenderer: headerRenderer,
      width: 60,
      locked: true,
      getRowMetaData: (rowData: any, column, rowIdx) => ({
        options: column.options,
        rowData: rowData,
        cellIndex: `select-row-cell-${rowIdx}-${column.id}`,
      }),
      tabSkip: true,
    };
  }, [enableRowSelect, enableSelectAll, rowSelection]);
};

const useReactDataGridColumnMetrics = (
  columns: Array<any>,
  {
    onColumnResize: onColumnResizeFromProps,
    gridRef,
    minWidth = 200,
    rowSelection,
    enableRowSelect,
    enableSelectAll,
  }: {
    onColumnResize: Function;
    gridRef: MutableRefObject<any>;
    minWidth?: number;
    rowSelection: IRowSelection;
    enableRowSelect?: boolean | string;
    enableSelectAll?: boolean;
  }
) => {
  const selectCellRequired: boolean =
    (enableRowSelect && !rowSelection) || (rowSelection && rowSelection.showCheckbox !== false);

  const [prevColumns, setPrevColumns] = useState<Array<IEditableDataGridColumnDef>>([]);
  const [prevComputedColumns, setPrevComputedColumns] = useState<Array<any>>();
  const [previousCellRequired, setPreviousCellRequired] = useState(selectCellRequired);

  const rowCheckboxCell = useRowCheckboxCell({
    rowSelection,
    enableRowSelect,
    enableSelectAll,
  });

  const { propColumns, computedColumns } = useMemo(() => {
    if (
      ColumnMetrics.sameColumns(prevColumns, columns, ColumnMetrics.sameColumn) &&
      selectCellRequired === previousCellRequired
    ) {
      return { propColumns: prevColumns, computedColumns: prevComputedColumns };
    }

    let cols: any = columns.slice(0);
    let unshiftedCols = {};
    if (selectCellRequired) {
      if (rowSelection.checkboxSide === 'right') {
        unshiftedCols = cols.push(rowCheckboxCell);
      } else {
        unshiftedCols = cols.unshift(rowCheckboxCell);
      }
      cols = (unshiftedCols as unknown as number) > 0 ? cols : unshiftedCols;
    }

    setPrevColumns(columns);
    setPrevComputedColumns(cols);
    setPreviousCellRequired(selectCellRequired);

    return { propColumns: columns, computedColumns: cols };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, selectCellRequired]);

  //Fall back on an arbitrarily high width (8192 should be a reasonable limit for a few years...) for the initial grid width.
  //If we pick a width that's too small, then columns sometimes get misaligned or render too small,
  // even though the column metrics are correct on the actual render.
  const currentGridWidth = gridRef.current?.parentElement?.offsetWidth ?? 8192;
  const [lastValidGridWidth, setLastValidGridWidth] = useState(currentGridWidth);

  const scrollBarWidth = getScrollbarSize();

  const calculatedColumnMetrics = useMemo(() => {
    return ColumnMetrics.recalculate({
      columns: computedColumns,
      totalWidth: minWidth > lastValidGridWidth ? minWidth - scrollBarWidth : lastValidGridWidth - scrollBarWidth,
      minColumnWidth: 40,
    });
  }, [lastValidGridWidth, computedColumns, minWidth]);

  const [columnMetrics, setColumnMetrics] = useState<ColumnMetricsType>(calculatedColumnMetrics);

  useEffect(() => {
    if (propColumns) {
      setColumnMetrics(calculatedColumnMetrics);
    }
  }, [propColumns, calculatedColumnMetrics]);

  useEffect(() => {
    const metricsUpdated = (entries: Array<any>) => {
      const offsetWidth = entries[0].target.offsetWidth;
      if (offsetWidth > 0) {
        setLastValidGridWidth(offsetWidth);
      }
    };

    const observer = new ResizeObserver(metricsUpdated);
    observer.observe(gridRef.current.parentElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onColumnResize = useCallback(
    (index: number, width: number) => {
      setColumnMetrics(ColumnMetrics.resizeColumn(columnMetrics, index, width));
      if (onColumnResizeFromProps) {
        onColumnResizeFromProps(index, width);
      }
    },
    [columnMetrics, onColumnResizeFromProps]
  );

  //Wrap with refFunction to ensure we get the up-to-date state when called from somewhere that isn't re-created on each render.
  const getGridWidth = useRefFunction(() => {
    return lastValidGridWidth;
  });

  return useMemo(() => {
    return { columnMetrics, onColumnResize, getGridWidth: getGridWidth };
  }, [columnMetrics, getGridWidth, onColumnResize]);
};

export default forwardRef(
  (
    props: Omit<
      IReactDataGridProps,
      | 'selected'
      | 'setSelected'
      | 'columnMetrics'
      | 'getGridWidth'
      | 'gridRef'
      | 'handleSort'
      | 'keysDownRef'
      | 'selectedRows'
    > &
      Partial<IReactDataGridDefaultedProps>,
    ref: any
  ) => {
    const [selected, setSelected] = useState<SelectedType>({ rowIdx: -1, idx: -1, active: false });

    const [sortDirection, setSortDirection] = useState<Direction | undefined>(undefined);
    const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);

    const gridRef = useRef<any>();

    //Not 100% that this should be 'direction'.
    const handleSort = useCallback(
      (columnKey: string, direction: Direction) => {
        setSortDirection(direction);
        setSortColumn(columnKey);
        props.onGridSort(columnKey, direction);
      },
      [props]
    );

    useEffect(() => {
      setSortColumn(props.sortColumn);
      setSortDirection(props.sortDirection);
    }, [props.sortColumn, props.sortDirection]);

    const rowKey = props.rowKey || 'id';

    const { columnMetrics, onColumnResize, getGridWidth } = useReactDataGridColumnMetrics(props.columns, {
      onColumnResize: props.onColumnResize,
      gridRef: gridRef,
      minWidth: props.minWidth,
      enableRowSelect: props.enableRowSelect,
      enableSelectAll: props.enableSelectAll,
      rowSelection: props.rowSelection,
    });

    const { handleRowSelect, keysDownRef, selectedRows } = useCheckboxRowSelection({
      rowSelection: props.rowSelection,
      enableRowSelect: props.enableRowSelect,
      setSelected,
      rowKey,
      onRowSelect: props.onRowSelect,
      rowGetter: props.rowGetter,
    });

    //Memo wrapper on context to prevent unnecessary additional renders of consumers.
    const reactDataGridContext = useMemo(
      () => ({
        handleRowSelect,
        handleSort,
      }),
      [handleRowSelect, handleSort]
    );

    return (
      <ReactDataGridContext.Provider value={reactDataGridContext}>
        <ReactDataGrid
          ref={ref}
          {...props}
          selected={selected}
          setSelected={setSelected}
          rowKey={rowKey}
          rowHeight={props.rowHeight || 36}
          columnMetrics={columnMetrics}
          onColumnResize={onColumnResize}
          getGridWidth={getGridWidth}
          gridRef={gridRef}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          handleSort={handleSort}
          keysDownRef={keysDownRef}
          selectedRows={selectedRows}
        />
      </ReactDataGridContext.Provider>
    );
  }
);
